import type { IdMap } from '@b-stock/fe-core/bstock-next/queries'
import type { StoreFrontDto } from '@b-stock/listing-api-client'

import { fetchStorefrontsById } from '@/buyer-portal/queries/offers/utils/fetchStorefrontsById'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

/**
 * Resolves the storefront each of `listings` actually belongs to, so a batch
 * spanning many storefronts at once (e.g. the All Listings Page) can
 * classify each listing by its own storefront rather than by whichever
 * single storefront the page itself happens to be scoped to, if any. Skips
 * the extra bulk lookup for the common case — every listing already sharing
 * the page's own storefront — by reusing `pageStorefront` directly instead of
 * re-fetching it. Reuses the buyer-portal's own storefronts-by-id helper
 * (offerSearchQuery's dependency) rather than a second copy of the same
 * fetchAllResultsById/getStorefrontsClient wiring. Shared by
 * fetchMobileManifestItems (manifest-line fetching) and fetchMobileListingIds
 * (title mobile-vs-general classification) — both need the same per-listing
 * storefront, resolved the same fail-soft way.
 */
export const resolveStorefrontsForListings = async (
  accessToken: string | null,
  listings: CombinedListingSearchResult[],
  pageStorefront: StoreFrontDto | undefined
): Promise<IdMap<StoreFrontDto>> => {
  const storefrontIds = new Set(listings.map((listing) => listing.storefrontId))
  if (
    pageStorefront?._id &&
    storefrontIds.size === 1 &&
    storefrontIds.has(pageStorefront._id)
  ) {
    return { [pageStorefront._id]: pageStorefront }
  }

  try {
    return await fetchStorefrontsById([...storefrontIds], accessToken)
  } catch (error) {
    // Fail soft — treat every listing as non-mobile rather than letting a
    // storefront lookup failure break the caller's whole operation. Logged
    // (Datadog picks up console.warn) so an actual prod failure rate here
    // isn't invisible.
    console.warn(
      '[resolveStorefrontsForListings] storefront lookup failed, treating batch as non-mobile',
      error
    )
    return {}
  }
}
